import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { BooleanParam, NumberParam, useQueryParams } from "use-query-params";
import { useAppConfig } from "../hooks/useAppConfig";
import { useMobileBlockScreen } from "../hooks/useMobileView";
import {
    useApplicationView,
    ApplicationView,
} from "../hooks/useApplicationView";
import { usePageTracking } from "../hooks/usePageTracking";
import { LaptopIcon } from "../icons";
import { clearRedirectPath } from "../lib/redirectAfterLogin";
import { useTypedFlags } from "../hooks/useTypedFlags";
import { usePlainChatSetup } from "../hooks/usePlainChatSetup";
import { useFinancialAccounts } from "../hooks/useFinancialAccounts";
import { MemberManagementProvider } from "./memberManagement/MemberManagementProvider";
import { NavigationProvider } from "./navigation/NavigationContext";
import { SettingsProvider } from "./settings/SettingsProvider";
import { usePrefetchTransactions } from "./transactions/usePrefetchTransactions";
import { AddTransactionModal } from "./transactions/AddTransactionsModal/AddTransactionsModal";
import { ImporterModal } from "./importer/ImporterModal";
import { NewConnectionHandler } from "./plaid/NewConnection/NewConnectionHandler";
import { MobileBlock } from "./general/MobileBlock/MobileBlock";
import { OnboardingRoutes } from "./routes/OnboardingRoutes";
import { MainRoutes } from "./routes/MainRoutes";
import { ReconciliationModal } from "./reconciliation/ReconciliationModal";
import { PersonalViewRoutes } from "./routes/PersonalViewRoutes";

const ALLOWED_ON_TABLET = ["/onboarding"];

export const PrivateInterface: React.FC = () => {
    const [redirectChecked, setRedirectChecked] = useState(false);
    const showMobileBlockScreen = useMobileBlockScreen();
    const routeGroup = useApplicationView();
    // The useAppConfig call is to ensure that the app is observing the query, and the data does not get cleared after the default gc time elapses 5min
    useAppConfig();
    const location = useLocation();
    usePageTracking();
    usePrefetchTransactions();
    usePlainChatSetup();
    const { manualTransactions: canAddManualTransaction } = useTypedFlags();

    const [
        {
            openTransactionsModal,
            openImporterModal,
            reconciledFinancialAccountId,
        },
        setSearchParams,
    ] = useQueryParams({
        openTransactionsModal: BooleanParam,
        openImporterModal: BooleanParam,
        reconciledFinancialAccountId: NumberParam,
    });

    const showMobileBlock = useMemo(
        () =>
            showMobileBlockScreen &&
            !ALLOWED_ON_TABLET.some((p) => location.pathname.startsWith(p)),
        [showMobileBlockScreen, location.pathname],
    );

    useEffect(() => {
        setRedirectChecked(true);
    }, []);

    useEffect(clearRedirectPath, []);

    const financialAccounts = useFinancialAccounts();
    const reconciledFinancialAccount = reconciledFinancialAccountId
        ? financialAccounts.find(
              (account) => account.id === reconciledFinancialAccountId,
          )
        : undefined;

    if (!redirectChecked) {
        return null;
    }

    const renderRoutes = () => {
        switch (routeGroup) {
            case ApplicationView.ONBOARDING:
                return <OnboardingRoutes />;
            case ApplicationView.MAIN:
                return <MainRoutes />;
            case ApplicationView.PERSONAL_VIEW:
                return <PersonalViewRoutes />;
            default:
                return null;
        }
    };

    return (
        <NavigationProvider>
            <MemberManagementProvider>
                <SettingsProvider>
                    {renderRoutes()}

                    {showMobileBlock && (
                        <MobileBlock
                            fullscreen
                            icon={<LaptopIcon />}
                            heading="Kick isn't available on mobile just yet"
                            showLogoutButton
                        >
                            <p>Please log in on a desktop browser.</p>
                        </MobileBlock>
                    )}
                </SettingsProvider>
            </MemberManagementProvider>

            <NewConnectionHandler />

            <AddTransactionModal
                show={!!openTransactionsModal && canAddManualTransaction}
                onHide={() =>
                    setSearchParams((current) => ({
                        ...current,
                        openTransactionsModal: undefined,
                    }))
                }
            />

            <ImporterModal
                show={!!openImporterModal}
                onHide={() =>
                    setSearchParams((current) => ({
                        ...current,
                        openImporterModal: undefined,
                    }))
                }
            />

            {reconciledFinancialAccount && (
                <ReconciliationModal
                    show={!!reconciledFinancialAccount}
                    reconciledFinancialAccount={reconciledFinancialAccount}
                    onHide={() =>
                        setSearchParams((current) => ({
                            ...current,
                            reconciledFinancialAccountId: undefined,
                        }))
                    }
                />
            )}
        </NavigationProvider>
    );
};
